import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { LanguageProvider } from "./context/LanguageContext";

// Intercepter uniquement les erreurs CORS de YouTube (pas tous les événements)
if (typeof window !== 'undefined') {
  const originalErrorHandler = window.onerror;
  
  window.onerror = function(message, source, lineno, colno, error) {
    // Filtrer uniquement les erreurs YouTube/CORS
    if (message === 'Script error.' || 
        (source && (source.includes('youtube') || source.includes('googlevideo'))) ||
        (message && typeof message === 'string' && message.includes('youtube'))) {
      return true; // Supprime l'erreur
    }
    // Laisser passer toutes les autres erreurs
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }
    return false;
  };
}

// Import components
import Header from "./components/HeaderNew";
import Hero from "./components/Hero";
import Welcome from "./components/Welcome";
import LineagesHistory from "./components/LineagesHistory";
import EventsAgenda from "./components/EventsAgenda";
import Statistics from "./components/Statistics";
import News from "./components/News";
import NewsletterSubscription from "./components/NewsletterSubscription";
import Footer from "./components/Footer";
import AuthErrorHandler from "./components/AuthErrorHandler";
import ErrorBoundary from "./components/ErrorBoundary";
import TranslateAlert from "./components/TranslateAlert";

// Import pages
import Lignages from "./pages/Lignages";
import Publications from "./pages/Publications";
import PublicationsCategories from "./pages/PublicationsCategories";
import PublicationsByCategory from "./pages/PublicationsByCategory";
import Contact from "./pages/Contact";
import Association from "./pages/Association";
import Prerogatives from "./pages/Prerogatives";
import JeanIIDeBrabant from "./pages/JeanIIDeBrabant";
import FamillesBlasons from "./pages/FamillesBlasons";
import But from "./pages/But";
import Admission from "./pages/Admission";
import Conseil from "./pages/Conseil";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import AdminDashboard from "./pages/AdminDashboard";
import EspaceMembre from "./pages/EspaceMembre";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordConfirm from "./pages/ResetPasswordConfirm";
import InstallationComplete from "./pages/InstallationComplete";
import SelectAccount from "./pages/SelectAccount";
import Logout from "./pages/Logout";
import ScanPage from "./pages/ScanPage";
import AllDocuments from "./pages/AllDocuments";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log(response.data.message);
    } catch (e) {
      console.error(e, `errored out requesting / api`);
    }
  };

  useEffect(() => {
    helloWorldApi();
    
    // Handle anchor navigation after page load
    const hash = window.location.hash;
    if (hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <TranslateAlert />
      <main>
        <Hero />
        <Welcome />
        <LineagesHistory />
        <News />
        <Statistics />
        <EventsAgenda />
      </main>
      <NewsletterSubscription />
      <Footer />
    </div>
  );
};

// Layout component for inner pages
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <TranslateAlert />
          <Routes>
          {/* Routes publiques SANS AuthErrorHandler */}
          <Route path="/" element={<ErrorBoundary><Home /></ErrorBoundary>} />
          <Route path="/lignages" element={<ErrorBoundary><Layout><Lignages /></Layout></ErrorBoundary>} />
          <Route path="/prerogatives" element={<ErrorBoundary><Layout><Prerogatives /></Layout></ErrorBoundary>} />
          <Route path="/jean-ii-brabant" element={<ErrorBoundary><Layout><JeanIIDeBrabant /></Layout></ErrorBoundary>} />
          <Route path="/familles-blasons" element={<ErrorBoundary><Layout><FamillesBlasons /></Layout></ErrorBoundary>} />
          <Route path="/but" element={<ErrorBoundary><Layout><But /></Layout></ErrorBoundary>} />
          <Route path="/admission" element={<ErrorBoundary><Layout><Admission /></Layout></ErrorBoundary>} />
          <Route path="/conseil" element={<ErrorBoundary><Layout><Conseil /></Layout></ErrorBoundary>} />
          <Route path="/publications-categories" element={<ErrorBoundary><Layout><PublicationsCategories /></Layout></ErrorBoundary>} />
          <Route path="/publications" element={<ErrorBoundary><Layout><Publications /></Layout></ErrorBoundary>} />
          <Route path="/publications/:category" element={<ErrorBoundary><Layout><PublicationsByCategory /></Layout></ErrorBoundary>} />
          <Route path="/association" element={<ErrorBoundary><Layout><Association /></Layout></ErrorBoundary>} />
          <Route path="/contact" element={<ErrorBoundary><Layout><Contact /></Layout></ErrorBoundary>} />
          <Route path="/privacy" element={<ErrorBoundary><Layout><Privacy /></Layout></ErrorBoundary>} />
          <Route path="/terms" element={<ErrorBoundary><Layout><Terms /></Layout></ErrorBoundary>} />
          <Route path="/scan" element={<ErrorBoundary><ScanPage /></ErrorBoundary>} />
          <Route path="/all_doc" element={<ErrorBoundary><AllDocuments /></ErrorBoundary>} />
          <Route path="/logout" element={<Logout />} />
          
          {/* Routes authentifiées AVEC AuthErrorHandler */}
          <Route path="/admin/*" element={
            <AuthErrorHandler>
              <AdminDashboard />
            </AuthErrorHandler>
          } />
          <Route path="/membre/*" element={
            <AuthErrorHandler>
              <EspaceMembre />
            </AuthErrorHandler>
          } />
          <Route path="/espace-membre" element={
            <AuthErrorHandler>
              <EspaceMembre />
            </AuthErrorHandler>
          } />
          <Route path="/select-account" element={
            <AuthErrorHandler>
              <SelectAccount />
            </AuthErrorHandler>
          } />
          <Route path="/login" element={
            <AuthErrorHandler>
              <EspaceMembre />
            </AuthErrorHandler>
          } />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password-confirm" element={<ResetPasswordConfirm />} />
          <Route path="/installation-complete" element={<InstallationComplete />} />
          {/* Additional routes can be added here as needed */}
        </Routes>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
